import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';

const HeartMonitoringTab = ({ user, setResult }) => {
  const { t } = useTranslation();
  const [restingHR, setRestingHR] = useState('');
  const [maxHR, setMaxHR] = useState('');
  const [systolicBP, setSystolicBP] = useState('');
  const [diastolicBP, setDiastolicBP] = useState('');
  const [hrv, setHrv] = useState('');
  const [heartSymptoms, setHeartSymptoms] = useState([]);
  const [loading, setLoading] = useState(false);

  const heartSymptomsList = [
    'Chest Pain', 'Shortness of Breath', 'Dizziness', 'Irregular Heartbeat',
    'Palpitations', 'Fatigue', 'Chest Discomfort', 'None'
  ];

  const toggleHeartSymptom = (symptom) => {
    if (heartSymptoms.includes(symptom)) {
      setHeartSymptoms(heartSymptoms.filter(s => s !== symptom));
    } else {
      setHeartSymptoms([...heartSymptoms, symptom]);
    }
  };

  const handleHeartAnalysis = async () => {
    if (!restingHR) {
      toast.error('Please enter your resting heart rate');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/heart/analyze`, {
        metrics: {
          resting_heart_rate: parseInt(restingHR),
          max_heart_rate: maxHR ? parseInt(maxHR) : null,
          systolic_bp: systolicBP ? parseInt(systolicBP) : null,
          diastolic_bp: diastolicBP ? parseInt(diastolicBP) : null,
          heart_rate_variability: hrv ? parseFloat(hrv) : null,
          exercise_heart_rate: null
        },
        symptoms: heartSymptoms.filter(s => s !== 'None'),
        medical_history: [],
        medications: [],
        lifestyle_factors: {}
      });
      
      setResult({ type: 'heart', data: response.data.analysis });
      toast.success('Heart health analysis complete!');
    } catch (error) {
      toast.error('Failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Resting Heart Rate (bpm) *
          </label>
          <input
            type="number"
            value={restingHR}
            onChange={(e) => setRestingHR(e.target.value)}
            placeholder="72"
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-red-500"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Max Heart Rate (bpm)
          </label>
          <input
            type="number"
            value={maxHR}
            onChange={(e) => setMaxHR(e.target.value)}
            placeholder="180"
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-red-500"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            HRV (ms)
          </label>
          <input
            type="number"
            value={hrv}
            onChange={(e) => setHrv(e.target.value)}
            placeholder="45"
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-red-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Systolic BP (mmHg)
          </label>
          <input
            type="number"
            value={systolicBP}
            onChange={(e) => setSystolicBP(e.target.value)}
            placeholder="120"
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-red-500"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Diastolic BP (mmHg)
          </label>
          <input
            type="number"
            value={diastolicBP}
            onChange={(e) => setDiastolicBP(e.target.value)}
            placeholder="80"
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-red-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Current Symptoms
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {heartSymptomsList.map((symptom) => (
            <button
              key={symptom}
              onClick={() => toggleHeartSymptom(symptom)}
              className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                heartSymptoms.includes(symptom)
                  ? 'bg-red-100 border-red-500 text-red-700'
                  : 'bg-white border-gray-300 hover:border-red-300'
              }`}
            >
              {symptom}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          onClick={handleHeartAnalysis}
          disabled={loading || !restingHR}
          className="flex-1 bg-red-600 hover:bg-red-700"
        >
          {loading ? 'Analyzing...' : 'Analyze Heart Health'}
        </Button>
        <Button
          onClick={() => toast.info("Check your history in the dedicated page")}
          variant="outline"
          className="border-red-600 text-red-600"
        >
          History
        </Button>
      </div>
    </div>
  );
};

export default HeartMonitoringTab;
